import {useCallback, useEffect, useRef} from "react"
import {useQuery, useQueryClient} from "@tanstack/react-query"
import {chatServiceV2, coreSocket} from "services"
import {SOCKET_ROOM_PRIORITY_TABLE, SocketEvent} from "helpers/socket"
import {Communication} from "types/communication"
import {DEPARTMENT_ROUTING_INFO_QUERY_KEY, isStatusUpdateForStudent} from "./departmentRoutingRail.helpers"

type Params = {
    roomId: number | string
    /** Only department rooms opened by staff/admin (see `canRequestDepartmentRouting`) */
    enabled: boolean
    /** Profiles of the room's student, to match core status-change events */
    studentProfileIds: number[]
    viewAsUserId?: number | null
}

/**
 * Loads "who is handling the conversation" for a department room (chat-api `POST /rooms/department/routing`).
 * Refetched when the room opens, when the student's status changes (core socket) and, through `refresh`, on new
 * student messages. chat-api caches the result, so refetching is cheap.
 */
export const useDepartmentRoutingInfo = ({roomId, enabled, studentProfileIds, viewAsUserId}: Params) => {
    const queryClient = useQueryClient()
    const numericRoomId = Number(roomId)

    const query = useQuery<Communication.DepartmentRoutingInfo | null>(
        [DEPARTMENT_ROUTING_INFO_QUERY_KEY, numericRoomId, viewAsUserId ?? null],
        () => chatServiceV2.getDepartmentRoutingInfo(numericRoomId),
        {
            enabled,
            staleTime: 0,
            retry: false,
            refetchOnWindowFocus: false
        }
    )

    const enabledRef = useRef(enabled)
    enabledRef.current = enabled

    const refresh = useCallback(
        (targetRoomId: number | string = numericRoomId) => {
            if (!enabledRef.current) {
                return
            }
            queryClient.invalidateQueries([DEPARTMENT_ROUTING_INFO_QUERY_KEY, Number(targetRoomId)])
        },
        [queryClient, numericRoomId]
    )

    const studentProfileIdsRef = useRef(studentProfileIds)
    studentProfileIdsRef.current = studentProfileIds

    useEffect(() => {
        if (!enabled) {
            return
        }
        const handleStudentStatusUpdated = (data?: {profileId?: number}) => {
            if (isStatusUpdateForStudent(data?.profileId, studentProfileIdsRef.current)) {
                refresh(numericRoomId)
            }
        }
        // Core emits `Profile.StudentStatus.Updated` to the priority table room
        try {
            coreSocket.on(SocketEvent.ProfileStudentStatusUpdated, handleStudentStatusUpdated)
            coreSocket.joinRoom(SOCKET_ROOM_PRIORITY_TABLE).catch(() => undefined)
        } catch {
            // The core socket is not initialized: the rail still refreshes on room open and new messages
            return
        }
        return () => {
            try {
                coreSocket.off(SocketEvent.ProfileStudentStatusUpdated, handleStudentStatusUpdated)
                coreSocket.leaveRoom(SOCKET_ROOM_PRIORITY_TABLE).catch(() => undefined)
            } catch {
                // ignore: socket already torn down
            }
        }
    }, [enabled, numericRoomId, refresh])

    return {routingInfo: enabled ? query.data ?? null : null, refresh}
}
